// @ flow
import React, {useEffect, useRef, useState} from "react";
import {useHistory} from "react-router-dom";
import {useFormik} from "formik";
import {Row, Col, Form, Input, Select, Button, List, Skeleton, Switch} from "antd";
import MaskedInput from "antd-mask-input";
import {NotificationOutlined} from "@ant-design/icons";

import {useTranslation} from "react-i18next";

import {useStore} from "../../../hooks/useStore";
import {useFetch} from "../../../hooks/useFetch";
import {newUserValidation} from "./newUserValidation";
import {newUserFormType, editUserPropType} from "./newUserPropTypes";
import {ErrorFocus} from "../../_defaultComponents/ErrorFocus/ErrorFocus";
import {CustomButton} from "../../_defaultComponents/CustomButton/CustomButton";
import {userTypes} from "../../../constants/constants";
import styled from "styled-components";
import {
    CommunicationPreferencesCardContainer,
    HeaderText
} from "../../_defaultComponents/CommunicationPreferencesCard/CpStyles";
import {CityFilter} from "../../_defaultComponents/FilterComponents";
import {groupBy} from "lodash";
import {arrayNaturalSort} from "../../../utils/helpers";

export const NewUser = ({userId}: editUserPropType) => {
    const stores = useStore();
    const {t} = useTranslation(["newUser", "userTypes", "communicationPreferences"]);
    const history = useHistory();
    const [companies, setCompanies] = useState([]);
    const [cities, setCities] = useState([]);
    const [upperGroups, setUpperGroups] = useState([]);
    const [recall, setRecall] = useState(true);
    const [uatSwitch, setUatSwitch] = useState(false);
    const [isTest, setIsTest] = useState(false);
    const recallRef = useRef(recall);
    recallRef.current = recall;

    const [dailyReminder, setDailyReminder] = useState(false);

    useEffect(() => {
        setTimeout(() => {
            setRecall(!recallRef.current);
        }, 60000);
    }, [recall]);

    //seçilen alt firmalar (her row'da gösterebilmek için company bazında ayrıştırıldı)
    const [subCompanies, setSubCompanies] = useState([[]]);

    //ekrana giriş yapıldığından beri işlem yapılan company'lerin bütün alt firmaları
    const [allSubCompanies, setAllSubCompanies] = useState([[{name: "", _id: ""}]]);
    const [didSubCompaniesLoaded, setDidSubCompaniesLoaded] = useState(false);

    const [, user = {}, , setLoading] = useFetch({
        fetchFn: stores.userStore.getUser.bind(null, {
            userId
        }),
        showLoading: true,
        condition: !!userId,
        callbackFunc: response => {
            if (response?.data?.uat) {
                setUatSwitch(response?.data?.uat);
            }
            setIsTest(response?.data?.isTest ?? false);
        }
    });

    const [, companyList, ,] = useFetch({
        fetchFn: stores.companyStore.getCompanyList.bind(null, {
            limit: 3000,
            skip: 0,
            activity: "enabled",
            onboardingStatus: "all"
        }),
        showLoading: true
    });

    const [
        upperGroupsByCompanyAndCityLoading,
        upperGroupsByCompanyAndCity,
        setUpperGroupsByCompanyAndCity,
        setUpperGroupsByCompanyAndCityLoading
    ] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.regionStore.getUpperGroupsByCities.bind(null, {
            companyId: undefined /* "pool", open when is pool ready*/,
            cities: cities.length === 0 ? "none" : cities.join(".")
        }),
        dependencies: [cities],
        initialCall: true
    });

    const renderUpperGroupsByCityGrouping = (upperGroups, key) => {
        const groupByCity = groupBy(upperGroups, "city");
        let cityKeys = Object.keys(groupByCity).map(city => city.capitalize());
        cityKeys = cityKeys.sort((a, b) => a.localeCompare(b));
        let grouped;
        if (groupByCity && upperGroups != ["all"]) {
            grouped = cityKeys.map((city, i) => (
                <Select.OptGroup
                    label={city}
                    key={i}
                    data-testid={`${key}Select_optionGroup`}
                    data-testvalue={city}
                >
                    {arrayNaturalSort(groupByCity[city.toLocaleLowerCase("tr-TR")], "group").map(
                        upperGroup => (
                            <Select.Option
                                key={upperGroup.group}
                                value={upperGroup.group}
                                data-testid={`${key}Select_optionGroup`}
                            >
                                {upperGroup.group}
                            </Select.Option>
                        )
                    )}
                </Select.OptGroup>
            ));
        }
        return grouped;
    };
    const getSubCompany = async (company, index, cities = []) => {
        const result = await stores.companyStore.getSubCompanyList({
            companyId: company,
            limit: 1000,
            activity: "enabled"
        });

        const simpleRows = result?.rows
            ?.filter(row => (cities.length > 0 ? cities.includes(row.city) : true))
            .map(subcomp => ({
                name: subcomp.name,
                _id: subcomp._id,
                regionGroupId: subcomp.regionGroupId,
                upperRegionGroup: subcomp.upperRegionGroup,
                city: subcomp.city
            }));

        setAllSubCompanies(prev =>
            prev.map((item, prevIndex) => {
                return prevIndex === index ? simpleRows : item;
            })
        );
    };

    useEffect(() => {
        if (userId) {
            const getCommunicationPreferences = async () => {
                const resultOfRequest = await stores.userStore.getCommunicationPreferences({
                    userId
                });
                setDailyReminder(
                    resultOfRequest?.data.communicationPreferences.email.shift.dailyReminder
                );
            };
            getCommunicationPreferences();
        }
    }, [userId]);

    const formik = useFormik({
        initialValues: {
            username: "",
            email: "",
            role: "",
            fullName: "",
            password: "",
            phone: "",
            isTest: false,
            title: "",
            communicationPreferences: false,
            newUser: !user?.data?._id,
            auth: {
                companies: [],
                subcompanies: [],
                cities: [],
                upperGroups: [],
                canAssignForceCourier: false,
                canSplitDeliveries: false
            },
            ...(user && user.data)
        },
        validationSchema: newUserValidation,
        enableReinitialize: true,
        onSubmit: async (values: newUserFormType) => {
            await setLoading(true);
            const upperGroupCityValues = [];
            upperGroups.forEach(group => {
                const city = upperGroupsByCompanyAndCity.find(c => c.group === group).city;
                upperGroupCityValues.push({group, city});
            });
            values.auth.upperGroups = upperGroupCityValues;
            values.phone = values.phone.replace(/[-,(,)]/g, "");
            values.uat = uatSwitch;
            values.isTest = isTest;
            let companyError = "";

            if (["cUser", "sOperation"].includes(values.role)) {
                if (!companies[0]) {
                    companyError = t("newUser.validation.companyAndSubcompanyError", {
                        ns: "newUser"
                    });
                } else {
                    const nonEmptyCompanies = companies.filter(n => n);
                    values.auth.companies = nonEmptyCompanies;
                    let unparsedSubCompanies = [];
                    subCompanies.map(subComp => subComp.map(sub => unparsedSubCompanies.push(sub)));
                    values.auth.subcompanies = unparsedSubCompanies;
                }
            } else if (["oUser"].includes(values.role)) {
                const nonEmptyCompanies = companies.filter(n => n);
                values.auth.companies = nonEmptyCompanies;
                let unparsedSubCompanies = [];
                subCompanies.map(subComp => subComp.map(sub => unparsedSubCompanies.push(sub)));
                values.auth.subcompanies = unparsedSubCompanies;
                values.auth.cities = cities;
            }

            if (companyError) {
                stores.appStore.openErrorModal({content: companyError});
            } else {
                if (userId) {
                    delete values.password;
                    const result = await stores.userStore.updateUser({user: values});
                    await setLoading(false);
                    if (result) {
                        history.push("/users/" + userId);
                    }
                    const preferences = {
                        communicationPreferences: {
                            email: {
                                shift: {
                                    dailyReminder: dailyReminder
                                }
                            }
                        }
                    };
                    await stores.userStore.updateCommunicationPreferences({
                        userId,
                        preferences
                    });
                } else {
                    const result = await stores.userStore.createUser(values);
                    const preferences = {
                        communicationPreferences: {
                            email: {
                                shift: {
                                    dailyReminder: values.communicationPreferences
                                }
                            }
                        }
                    };
                    const userId = result._id;
                    await stores.userStore.updateCommunicationPreferences({
                        userId,
                        preferences
                    });

                    await setLoading(false);
                    if (result) {
                        history.push("/users/" + result?._id);
                    }
                }
            }
            setLoading(false);
        }
    });
    const {
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        isSubmitting,
        setFieldValue
    } = formik;

    useEffect(() => {
        //eski companies verisi varsa ve yeni eklenmemişse
        if (values.auth.companies[0] && !companies[0]) {
            //eskiyi yeniye ata
            setCompanies(values.auth.companies);

            values.auth.companies.map((comp, index) => {
                //company'lerin bütün sub'larını çek, alt firma seçimini getireceğimiz için lazım
                //bunu yaparken her seferinde default değeri önceden ata
                setAllSubCompanies(prev => [...prev, [{name: "", _id: ""}]]);
                getSubCompany(comp, index, values.auth.cities);
            });
            //eski subcompanies verisi varsa
        } else if (values.role == "oUser" && companies.length == 0) {
            setCompanies([""]);
        }
        if (values.auth.cities[0] && !cities[0]) {
            setCities(values.auth.cities);
        }
        if (
            ["cUser", "sOperation"].includes(values.role) &&
            values.auth.companies.length === 0 &&
            companies.length === 0
        ) {
            setCompanies([{}]);
        }
        if (
            values.role == "oUser" &&
            values.auth.upperGroups &&
            values.auth.upperGroups.length > 0
        ) {
            setUpperGroups(values.auth.upperGroups.map(upperGroup => upperGroup.group));
        }
    }, [values]);

    useEffect(() => {
        //eski subcompanies'i varsa ve bunlar state'e henüz atanmamışsa gir, company çekilirken eşleştir.
        //her row'da gösterebilmek için subCompanies state'ini; values.auth.subcompanies'i company bazında ayrıştırarak doldur.

        if (!didSubCompaniesLoaded && values.auth.subcompanies[0]) {
            //ilk yükleme olup olmadığının tespiti checkerForLoading;
            //daha sonraki değişikliklerde useEffect'e girmesi veriyi bozar, bu yüzden yalnızca initial loading'de girmesini sağla.
            let checkerForLoading = 0;

            const parsedSubCompanies = allSubCompanies.map((sub, index) =>
                sub
                    .map(s => s?._id)
                    .filter(s => values.auth.subcompanies.includes(s))
                    .concat(values.auth.subcompanies.filter(x => sub.includes(x)))
            );
            parsedSubCompanies.map(subComp => subComp.map(sub => checkerForLoading++));

            values.auth.subcompanies[0] &&
                checkerForLoading === values.auth.subcompanies.length &&
                setDidSubCompaniesLoaded(true);
            setSubCompanies(parsedSubCompanies);
        }
    }, [allSubCompanies]);

    const onChangeSwitch = async checked => {
        values.communicationPreferences = checked;
        setDailyReminder(checked);
    };

    return (
        <Row type="flex" justify="center" data-testid="newUserScreen">
            <Col span={20}>
                <Form
                    autoComplete="off"
                    data-testid="newUserForm"
                    labelCol={{span: 24}}
                    wrapperCol={{span: 24}}
                    onFinish={handleSubmit}
                >
                    <ErrorFocus formik={formik} />
                    <Form.Item
                        label={t("fields.username", {ns: "common"})}
                        validateStatus={errors.username && touched.username ? "error" : "success"}
                        help={errors.username && touched.username ? errors.username : null}
                    >
                        <Input
                            name="username"
                            data-testid="userNameInput"
                            value={values.username}
                            onChange={handleChange}
                        />
                    </Form.Item>
                    <Form.Item
                        label={t("fields.fullname", {ns: "common"})}
                        data-testid="nameInput"
                        validateStatus={errors.fullName && touched.fullName ? "error" : "success"}
                        help={errors.fullName && touched.fullName ? errors.fullName : null}
                    >
                        <Input name="fullName" value={values.fullName} onChange={handleChange} />
                    </Form.Item>
                    <Form.Item
                        label="E-mail"
                        validateStatus={errors.email && touched.email ? "error" : "success"}
                        help={errors.email && touched.email ? errors.email : null}
                    >
                        <Input
                            name="email"
                            data-testid="emailInput"
                            value={values.email}
                            onChange={handleChange}
                        />
                    </Form.Item>
                    {!userId && (
                        <Form.Item
                            label={t("fields.password", {ns: "common"})}
                            validateStatus={
                                errors.password && touched.password ? "error" : "success"
                            }
                            help={errors.password && touched.password ? errors.password : null}
                        >
                            <Input.Password
                                name="password"
                                type="password"
                                data-testid="passwordInput"
                                value={values.password}
                                onChange={handleChange}
                            />
                        </Form.Item>
                    )}
                    {stores.meStore.checkRole("selectUserRole") && (
                        <Form.Item
                            label={t("fields.userRole", {ns: "common"})}
                            validateStatus={errors.role && touched.role ? "error" : "success"}
                            help={errors.role && touched.role ? errors.role : null}
                        >
                            <Select
                                showSearch
                                //disabled={userId}
                                name="roleSelect"
                                value={values.role}
                                onChange={value => setFieldValue("role", value)}
                                filterOption={(input, option) =>
                                    option.props.children
                                        .toLowerCase()
                                        .indexOf(input.toLowerCase()) >= 0 ||
                                    option.props.value.toLowerCase().indexOf(input.toLowerCase()) >=
                                        0
                                }
                            >
                                {userTypes.map(role => (
                                    <Select.Option
                                        key={role.translation_key}
                                        value={role.value}
                                        name="roleSelect_option"
                                    >
                                        {t(role.translation_key, {
                                            ns: "userTypes"
                                        })}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}
                    <>
                        <Row type="flex" justify="space-between">
                            <Col span="11">
                                <Form.Item
                                    label={t("fields.phoneNumber", {ns: "common"})}
                                    validateStatus={
                                        errors.phone && touched.phone ? "error" : "success"
                                    }
                                    help={errors.phone && touched.phone ? errors.phone : null}
                                >
                                    <MaskedInput
                                        name="phone"
                                        data-testid="phoneInput"
                                        placeholder="0(555)-555-5555"
                                        mask="1(111)-111-1111"
                                        value={values.phone}
                                        onChange={handleChange}
                                    />
                                </Form.Item>
                            </Col>
                            {["cUser", "sOperation"].includes(values.role) && (
                                <Col span="11">
                                    <Form.Item
                                        label={t("fields.title", {ns: "common"})}
                                        validateStatus={
                                            errors.title && touched.title ? "error" : "success"
                                        }
                                        help={errors.title && touched.title ? errors.title : null}
                                    >
                                        <Input
                                            name="title"
                                            data-testid="titleInput"
                                            value={values.title}
                                            onChange={handleChange}
                                        />
                                    </Form.Item>
                                </Col>
                            )}
                        </Row>
                    </>

                    {["oUser"].includes(values.role) ? (
                        <>
                            <Row type="flex" justify="space-between">
                                <Col span="24">
                                    <CityFilter
                                        showAllOption={false}
                                        value={cities}
                                        mode={"multiple"}
                                        onChange={value => {
                                            let errors = [];
                                            const selectedCompanies = companyList.rows.filter(x =>
                                                companies.includes(x._id)
                                            );
                                            if (value.length < cities.length) {
                                                const removedCity = cities.filter(
                                                    x => !value.includes(x)
                                                )[0];
                                                selectedCompanies.forEach((company, index) => {
                                                    const subCompanyList = allSubCompanies[
                                                        index
                                                    ].filter(x =>
                                                        subCompanies[index].includes(x._id)
                                                    );
                                                    const subCompaniesInThatCity = subCompanyList.filter(
                                                        x => x.city == removedCity
                                                    );
                                                    if (subCompaniesInThatCity.length > 0) {
                                                        errors.push({
                                                            type: "subCompany",
                                                            companyName: company.companyName,
                                                            list: subCompaniesInThatCity
                                                        });
                                                    }
                                                });

                                                const selectedUpperGroups = upperGroupsByCompanyAndCity.filter(
                                                    x =>
                                                        x.city === removedCity &&
                                                        upperGroups.includes(x.group)
                                                );
                                                if (selectedUpperGroups.length) {
                                                    errors.push({
                                                        type: "upperGroup",
                                                        list: selectedUpperGroups.map(x => ({
                                                            name: x.group
                                                        }))
                                                    });
                                                }
                                            }

                                            if (errors.length > 0) {
                                                let warningContent = t(
                                                    "validation.wrongSelections",
                                                    {ns: "newUser"}
                                                );
                                                warningContent += `\n\n`;
                                                errors.forEach(error => {
                                                    if (error.type == "upperGroup") {
                                                        error.list.forEach(item => {
                                                            warningContent += `${item.name}\n`;
                                                        });
                                                    }
                                                    if (error.type == "subCompany") {
                                                        warningContent += `\n[ ${error.companyName} ]\n`;
                                                        error.list.forEach(item => {
                                                            warningContent += `${item.name}\n`;
                                                        });
                                                        error.list.forEach(item => {
                                                            warningContent += `${item.name}\n`;
                                                        });
                                                        warningContent += "\n";
                                                    }
                                                });
                                                stores.appStore.openWarningModal({
                                                    title: t("modal.warningTitle", {ns: "common"}),
                                                    content: warningContent
                                                });
                                                return false;
                                            } else {
                                                setCities(value);

                                                companies.map((comp, index) => {
                                                    getSubCompany(comp, index, value);
                                                });
                                            }
                                        }}
                                    />
                                </Col>
                                <Col span={24}>
                                    <h5>{t("fields.upperGroup", {ns: "common"})}</h5>
                                    <SelectField
                                        value={upperGroups}
                                        data-testid="upperGroupSelect"
                                        onChange={value => {
                                            setUpperGroups(value);
                                        }}
                                        notFoundContent={t("filters.dataNotFound", {ns: "common"})}
                                        placeholder={t("filters.addFilter", {ns: "common"})}
                                        optionFilterProp="children"
                                        filterOption={(input, option) =>
                                            option.children
                                                ?.toString()
                                                .toLowerCase()
                                                .indexOf(input.toLowerCase()) >= 0
                                        }
                                        mode={"multiple"}
                                        filterSort={(optionA, optionB) =>
                                            optionA.children
                                                ?.toString()
                                                .toLowerCase()
                                                .localeCompare(
                                                    optionB.children.toString().toLowerCase()
                                                )
                                        }
                                    >
                                        <>
                                            {renderUpperGroupsByCityGrouping(
                                                upperGroupsByCompanyAndCity,
                                                "region"
                                            )}
                                        </>
                                    </SelectField>
                                </Col>
                            </Row>
                        </>
                    ) : null}

                    {values.role === "cUser" && (
                        <Row type="flex" justify="start">
                            <Col span={24}>
                                <Form.Item
                                    label={t("fields.canAssignForceCourier", {ns: "common"})}
                                >
                                    <Switch
                                        onClick={() => {
                                            setFieldValue(
                                                "auth.canAssignForceCourier",
                                                !values.auth.canAssignForceCourier
                                            );
                                        }}
                                        checked={values.auth.canAssignForceCourier}
                                    ></Switch>
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item label={t("fields.canSplitDeliveries", {ns: "common"})}>
                                    <Switch
                                        onClick={() => {
                                            setFieldValue(
                                                "auth.canSplitDeliveries",
                                                !values.auth.canSplitDeliveries
                                            );
                                        }}
                                        checked={values.auth.canSplitDeliveries}
                                    ></Switch>
                                </Form.Item>
                            </Col>
                        </Row>
                    )}

                    {["cUser", "oUser", "sOperation"].includes(values.role)
                        ? companies?.map((company, index) => (
                              <Row
                                  type="flex"
                                  key={"row" + company?._id + index}
                                  justify="space-between"
                              >
                                  <Col span="11">
                                      <Form.Item label={t("fields.company", {ns: "common"})}>
                                          <Select
                                              showSearch
                                              filterOption={(input, option) =>
                                                  option.props.children
                                                      .toLowerCase()
                                                      .indexOf(input.toLowerCase()) >= 0 ||
                                                  option.props.value
                                                      .toLowerCase()
                                                      .indexOf(input.toLowerCase()) >= 0
                                              }
                                              name="companies"
                                              value={
                                                  companyList?.rows?.find(
                                                      rowComp => rowComp?._id === company
                                                  )?.companyName
                                              }
                                              onChange={value => {
                                                  getSubCompany(value, index, cities);
                                                  setCompanies(prev =>
                                                      prev.map((item, prevIndex) =>
                                                          prevIndex === index ? value : item
                                                      )
                                                  );
                                                  setSubCompanies(prev =>
                                                      prev.map((item, prevIndex) =>
                                                          prevIndex === index ? [] : item
                                                      )
                                                  );
                                              }}
                                          >
                                              {companyList?.rows?.map(company =>
                                                  !companies.includes(company?._id) ? (
                                                      <Select.Option
                                                          value={company?._id}
                                                          name="company_option"
                                                          key={company?._id}
                                                      >
                                                          {company.companyName}
                                                      </Select.Option>
                                                  ) : null
                                              )}
                                          </Select>
                                          {index === companies.length - 1 ? (
                                              <>
                                                  <Button
                                                      onClick={() => {
                                                          setCompanies(prev => [...prev, ""]);
                                                          setSubCompanies(prev => [...prev, []]);
                                                          setAllSubCompanies(prev => [
                                                              ...prev,
                                                              [{name: "", _id: ""}]
                                                          ]);
                                                      }}
                                                      type="link"
                                                  >
                                                      +{" "}
                                                      {t("newUser.actions.addCompany", {
                                                          ns: "newUser"
                                                      })}
                                                  </Button>
                                              </>
                                          ) : null}

                                          {companies.length > 1 && (
                                              <Button
                                                  onClick={() => {
                                                      setCompanies(prev =>
                                                          prev.filter(
                                                              (item, prevIndex) =>
                                                                  prevIndex !== index
                                                          )
                                                      );
                                                      setSubCompanies(prev =>
                                                          prev.filter(
                                                              (item, prevIndex) =>
                                                                  prevIndex !== index
                                                          )
                                                      );
                                                      setAllSubCompanies(prev =>
                                                          prev.filter(
                                                              (item, prevIndex) =>
                                                                  prevIndex !== index
                                                          )
                                                      );
                                                  }}
                                                  type="link"
                                              >
                                                  {t("newUser.actions.removeCompany", {
                                                      ns: "newUser"
                                                  })}
                                              </Button>
                                          )}
                                      </Form.Item>
                                  </Col>
                                  <Col span="11">
                                      {company ? (
                                          <Form.Item
                                              label={t("fields.subcompany", {
                                                  ns: "common"
                                              })}
                                          >
                                              <Select
                                                  showSearch
                                                  name="subcompanies"
                                                  value={subCompanies[index]}
                                                  mode="multiple"
                                                  filterOption={(input, option) =>
                                                      option.props.children
                                                          .toLowerCase()
                                                          .indexOf(input.toLowerCase()) >= 0 ||
                                                      option.props.value
                                                          .toLowerCase()
                                                          .indexOf(input.toLowerCase()) >= 0
                                                  }
                                                  onChange={value => {
                                                      setSubCompanies(prev =>
                                                          prev.map((item, prevIndex) =>
                                                              prevIndex === index ? value : item
                                                          )
                                                      );
                                                  }}
                                              >
                                                  {allSubCompanies[index]?.map(subComp => (
                                                      <Select.Option
                                                          value={subComp?._id}
                                                          name="subcomp_option"
                                                      >
                                                          {subComp.name}
                                                      </Select.Option>
                                                  ))}
                                              </Select>
                                          </Form.Item>
                                      ) : null}
                                  </Col>
                                  {stores.meStore.checkRole("isAdmin") && (
                                      <Col>
                                          <span>Uat Bilgisi</span>
                                          <Switch
                                              checked={uatSwitch}
                                              onChange={checked => setUatSwitch(checked)}
                                              style={{marginLeft: "15px"}}
                                          />
                                      </Col>
                                  )}
                              </Row>
                          ))
                        : null}

                    <Col span="11" style={{marginTop: "20px"}}>
                        <span>{t("fields.testUser", {ns: "common"})}</span>
                        <Switch
                            checked={isTest}
                            onChange={() => setIsTest(prev => !prev)}
                            style={{marginLeft: "15px"}}
                        />
                    </Col>

                    <CommunicationPreferencesCardContainer
                        title={
                            <h4>
                                <NotificationOutlined />
                                <HeaderText>
                                    {t("communicationPreferences", {
                                        ns: "communicationPreferences"
                                    })}
                                </HeaderText>
                            </h4>
                        }
                    >
                        <List.Item>
                            <Skeleton loading={false} active>
                                <div>
                                    {t("communicationPermission", {ns: "communicationPreferences"})}
                                </div>
                            </Skeleton>
                            <Switch checked={dailyReminder} onChange={onChangeSwitch} />
                        </List.Item>
                    </CommunicationPreferencesCardContainer>

                    <Row type="flex" justify="end">
                        <CustomButton
                            data-testid="submitButton"
                            type="primary"
                            htmlType="submit"
                            disabled={isSubmitting}
                        >
                            {t("actions.save", {ns: "common"})}
                        </CustomButton>
                    </Row>
                </Form>
            </Col>
        </Row>
    );
};

export const SelectField = styled(Select)`
    width: 100%;
    @media (max-width: 768px) {
        display: flex;
        width: 100%;
        .ant-select-selector {
            min-width: 197px;
            font-size: 12px;
        }
    }
`;
export const Wrapper = styled.div`
    margin-top: 20px;
    padding: 20px;
    border: 1px solid #e8e8e8;
    border-radius: 5px;
    background-color: #f0f0f0;
`;
export const WrapperCompanies = styled.div`
    margin-top: 20px;
`;

export const WrapperUpperGroups = styled.div`
    margin-top: 10px;
    background-color: #d4d4d4;
    border: 1px solid #e8e8e8;
    border-radius: 5px;
    padding: 20px;
`;
